// AP Automation Agent UI (Polished)
// Additions: zero-touch rate gauge, aging bucket donut, payment timeline chart,
// bulk approve, comm template picker.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField, MenuItem,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, DonutChart, RadialGauge, StatusPill,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    apZeroTouch, apPaymentOpt, apVendorComm, apThreeWay, apRecurring, apAgingAlerts,
} from './api';

const TABS = ['Zero-Touch Processor', 'Payment Optimization', 'Vendor Communication', 'Three-Way Match', 'Recurring Patterns', 'AP Aging Alerts'];
const COMM_TYPES = ['payment_status', 'remittance', 'dispute', 'w9_request'];

export default function APAutomationAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [zeroTouch, setZeroTouch] = useState(null);
    const [paymentOpt, setPaymentOpt] = useState(null);
    const [vendorComm, setVendorComm] = useState(null);
    const [vendorId, setVendorId] = useState('');
    const [commType, setCommType] = useState('payment_status');
    const [threeWay, setThreeWay] = useState(null);
    const [billId, setBillId] = useState('');
    const [recurring, setRecurring] = useState(null);
    const [aging, setAging] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 1 && !paymentOpt) { const r = await run(() => apPaymentOpt()); if (r?.data?.success) setPaymentOpt(r.data.data); }
            else if (tab === 4 && !recurring) { const r = await run(() => apRecurring()); if (r?.data?.success) setRecurring(r.data.data); }
            else if (tab === 5 && !aging) { const r = await run(() => apAgingAlerts()); if (r?.data?.success) setAging(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Zero-Touch Rate', value: zeroTouch?.zero_touch_rate != null ? `${zeroTouch.zero_touch_rate}%` : '—', accent: 'primary' },
        { label: 'Open Bills', value: (paymentOpt?.items || []).length || '—', accent: 'info' },
        { label: 'Recurring Patterns', value: (recurring?.patterns || []).length || '—', accent: 'secondary' },
        { label: 'Aging Alerts', value: (aging?.alerts || []).length || '—', accent: (aging?.alerts || []).length > 0 ? 'warning' : 'success' },
    ];

    // Aging bucket donut
    const agingBucketData = aging?.buckets ? Object.entries(aging.buckets).map(([bucket, amount]) => ({
        name: bucket,
        value: amount || 0,
    })).filter(d => d.value > 0) : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Zero-Touch Bill Processor"
                            subtitle="End-to-end: OCR → vendor match → GL code → auto-approve → schedule payment"
                            actions={
                                <Button variant="contained" size="small" startIcon={<PlayArrowIcon />}
                                    onClick={async () => {
                                        const r = await run(() => apZeroTouch());
                                        if (r?.data?.success) setZeroTouch(r.data.data);
                                    }}>Process Bills</Button>
                            }
                        />
                        {zeroTouch ? (
                            <>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={4}>
                                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={600}>Zero-Touch Rate</Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <RadialGauge
                                                    value={zeroTouch.zero_touch_rate || 0}
                                                    label={zeroTouch.zero_touch_rate >= 70 ? 'Strong' : 'Needs Work'}
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <SummaryGrid cards={[
                                            { label: 'Bills Processed', value: (zeroTouch.bills || []).length, accent: 'primary' },
                                            { label: 'Auto-Approved', value: (zeroTouch.bills || []).filter(b => b.status === 'processed').length, accent: 'success' },
                                            { label: 'Needs Review', value: (zeroTouch.bills || []).filter(b => b.status !== 'processed').length, accent: 'warning' },
                                        ]} cols={3} />
                                    </Grid>
                                </Grid>
                                <AIAnalysisBox text={zeroTouch.ai_analysis} />
                                <DrillTable
                                    columns={[
                                        { key: 'bill_number', label: 'Bill #' },
                                        { key: 'vendor', label: 'Vendor' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                        { key: 'confidence', label: 'Confidence', render: r => (
                                            <Chip size="small"
                                                color={r.confidence === 'high' ? 'success' : r.confidence === 'medium' ? 'warning' : 'default'}
                                                label={r.confidence} />
                                        )},
                                    ]}
                                    rows={zeroTouch.bills || []}
                                    maxHeight={420}
                                />
                            </>
                        ) : (
                            <EmptyState message="Click Process Bills to run the zero-touch pipeline." />
                        )}
                    </Stack>
                );
            case 1:
                return paymentOpt ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Payment Optimization" subtitle="Optimize payment timing and capture early-pay discounts" />
                        <DrillTable
                            columns={[
                                { key: 'vendor', label: 'Vendor' },
                                { key: 'bill', label: 'Bill' },
                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                { key: 'due_date', label: 'Due' },
                                { key: 'discount_available', label: 'Discount', render: r => r.discount_available ? <Chip size="small" color="success" label={r.discount_available} /> : '—' },
                                { key: 'priority_score', label: 'Priority', align: 'right' },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Schedule</Button> },
                            ]}
                            rows={[...(paymentOpt.items || [])].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0))}
                            maxHeight={480}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 2:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Vendor Communication Agent" subtitle="Automated vendor correspondence drafting" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Vendor ID" size="small" value={vendorId} onChange={e => setVendorId(e.target.value)} />
                            <TextField label="Type" size="small" select value={commType}
                                onChange={e => setCommType(e.target.value)} sx={{ minWidth: 180 }}>
                                {COMM_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                            </TextField>
                            <Button variant="contained" size="small" disabled={!vendorId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => apVendorComm(vendorId, commType));
                                    if (r?.data?.success) setVendorComm(r.data.data);
                                }}>Draft Message</Button>
                        </Stack>
                        {vendorComm && <AIAnalysisBox text={vendorComm.drafted_message} title="Drafted Message" />}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Three-Way Match" subtitle="PO → Receipt → Bill matching" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Bill ID" size="small" value={billId} onChange={e => setBillId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!billId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => apThreeWay(billId));
                                    if (r?.data?.success) setThreeWay(r.data.data);
                                }}>Run Match</Button>
                        </Stack>
                        {threeWay && (
                            <>
                                <DrillTable
                                    columns={[
                                        { key: 'type', label: 'Match Type' },
                                        { key: 'status', label: 'Status', render: r => <StatusPill status={r.status || 'pending'} /> },
                                        { key: 'score', label: 'Score', align: 'right', render: r => r.score != null ? `${r.score}%` : '—' },
                                    ]}
                                    rows={[
                                        { type: 'PO Match', status: threeWay.po_match?.status, score: threeWay.po_match?.score },
                                        { type: 'Receipt Match', status: threeWay.receipt_match?.status, score: threeWay.receipt_match?.score },
                                        { type: 'Line Match', status: threeWay.line_match?.status, score: threeWay.line_match?.score },
                                    ]}
                                />
                                <AIAnalysisBox text={threeWay.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            case 4:
                return recurring ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Recurring AP Patterns" subtitle="Detect & automate recurring vendor bills" />
                        <DrillTable
                            columns={[
                                { key: 'vendor', label: 'Vendor' },
                                { key: 'frequency', label: 'Frequency' },
                                { key: 'avg_amount', label: 'Avg', align: 'right', render: r => fmtMoney(r.avg_amount) },
                                { key: 'last_date', label: 'Last' },
                                { key: 'next_expected', label: 'Next' },
                                { key: 'alert', label: 'Alert', render: r => r.alert ? <Chip size="small" color="warning" label={r.alert} /> : '—' },
                            ]}
                            rows={recurring.patterns || []}
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 5:
                return aging ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="AP Aging Alerts" subtitle="Proactive aging monitoring with action recommendations" />
                        {agingBucketData.length > 0 && (
                            <Grid container spacing={1}>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Aging Distribution</Typography>
                                        <DonutChart data={agingBucketData} height={240} formatter={v => fmtMoney(v)} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Buckets ($)</Typography>
                                        <SimpleBarChart
                                            data={agingBucketData}
                                            xKey="name"
                                            series={[{ key: 'value', label: 'Amount', color: CHART_COLORS.warning }]}
                                            height={240}
                                            yFormatter={v => fmtMoney(v)}
                                            showLegend={false}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'vendor', label: 'Vendor' },
                                { key: 'invoice', label: 'Invoice' },
                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                { key: 'days_overdue', label: 'Days Overdue', align: 'right' },
                                { key: 'bucket', label: 'Bucket', render: r => <Chip size="small" color={r.bucket === '90+' ? 'error' : 'warning'} label={r.bucket} /> },
                                { key: 'alert_type', label: 'Alert' },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined" color="error">Pay Now</Button> },
                            ]}
                            rows={aging.alerts || []}
                            maxHeight={420}
                        />
                        <AIAnalysisBox text={aging.recommendations} title="AI Recommendations" />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="AP Automation Agent"
            subtitle="Agentic Autonomous Workflow — zero-touch, payment optimization, 3-way match, aging alerts."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
